import React from "react";
import PropTypes from "prop-types";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Alert, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { BarChart3, ChevronLeft, Gauge, LineChart, MoreVertical } from "lucide-react";

import { useAuth } from "../../components/contexts/AuthContext";
import { getSeoProvider } from "@/utils/agencyFeatures";
import { getData } from "../../components/hooks/getData";
import { usePersistedDateSelection } from "../../hooks/usePersistedDateSelection";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { DataTable } from "../../components/DataTable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/DropdownMenu";
import SuccessPopup from "../../components/SuccessPopup";
import { GoogleIcon } from "../../components/icons/GoogleIcon";
import { GoogleAnalyticsIcon } from "../../components/icons/GoogleAnalyticsIcon";
import { brand } from "../../theme/theme";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { isClientReadOnly } from "../../utils/clientAccess";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { UnderlineTabs } from "../Clients/metrics/UnderlineTabs";
import { VisibilityPanel } from "../Clients/metrics/VisibilityPanel";
import { TrafficPanel } from "../Clients/metrics/TrafficPanel";
import { WebPerformancePanel } from "../Clients/metrics/WebPerformancePanel";
import { SiteAuditPanel } from "../Clients/metrics/SiteAuditPanel";
import { ConnectGoogleAnalyticsModal } from "../Clients/metrics/ConnectGoogleAnalyticsModal";
import { ConnectSearchConsoleModal } from "../Clients/metrics/ConnectSearchConsoleModal";
import { ConnectSeRankingModal } from "../Clients/metrics/ConnectSeRankingModal";

const ANALYTICS_TABS = [
  { value: "visibilidad", label: "Visibilidad" },
  { value: "trafico", label: "Tráfico y usuarios" },
  { value: "auditorias", label: "Auditorías" },
  { value: "rendimiento", label: "Rendimiento web" },
];

const TAB_SUBTITLES = {
  visibilidad: "Posiciones y visibilidad orgánica del cliente",
  trafico: "Tráfico y usuarios del cliente",
  rendimiento: "Rendimiento web del cliente",
  auditorias: "Auditoría técnica de la web del cliente",
};

const DEFAULT_PAGE_SIZE = 25;

const DATE_SELECTION_STORAGE_KEY = "markezy.analytics.dateSelection";

// Mismos iconos y modales que la sección Conexiones de la ficha del cliente.
const LINKABLE_CONNECTIONS = [
  {
    key: "ga4",
    label: "Google Analytics",
    icon: <GoogleAnalyticsIcon size={18} />,
    Modal: ConnectGoogleAnalyticsModal,
  },
  {
    key: "search_console",
    label: "Search Console",
    icon: <GoogleIcon size={16} />,
    Modal: ConnectSearchConsoleModal,
  },
  {
    key: "seranking",
    label: "SE Ranking",
    icon: <LineChart size={16} color="#2563EB" />,
    Modal: ConnectSeRankingModal,
  },
];

// PageSpeed no se enlaza: el análisis se puede lanzar siempre desde Rendimiento web.
const PAGESPEED = { label: "PageSpeed", icon: <Gauge size={16} color="#0F9D58" /> };

// El proveedor SEO se muestra sin marca a las agencias que no lo tienen
const useLinkableConnections = () => {
  const { user } = useAuth();
  const seoLabel = getSeoProvider(user).label;
  return LINKABLE_CONNECTIONS.map((connection) =>
    connection.key === "seranking" ? { ...connection, label: seoLabel } : connection
  );
};

const missingConnections = (client, connections) =>
  connections.filter(({ key }) => !client.integrations?.[key]);

const ConnectionsCell = ({ integrations }) => {
  const connections = useLinkableConnections();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      {[...connections, PAGESPEED].map(({ key, label, icon }) => {
        const connected = !key || integrations?.[key];
        return (
          <Tooltip key={label} title={connected ? label : `${label} (sin conectar)`} arrow>
            <Box
              sx={{
                display: "inline-flex",
                ...(!connected && { filter: "grayscale(1)", opacity: 0.4 }),
              }}
            >
              {icon}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

ConnectionsCell.propTypes = {
  integrations: PropTypes.shape({
    ga4: PropTypes.bool,
    search_console: PropTypes.bool,
    seranking: PropTypes.bool,
  }),
};

const ConnectMenu = ({ client, onConnect }) => {
  const missing = missingConnections(client, useLinkableConnections());
  if (missing.length === 0) return null;

  return (
    // El menú se renderiza en un portal pero sus eventos siguen subiendo por el
    // árbol de React: sin esto, cada clic abriría también el cliente.
    <Box onClick={(event) => event.stopPropagation()} sx={{ display: "inline-flex" }}>
      <DropdownMenu>
        <Tooltip title="Conectar" arrow>
          <DropdownMenuTrigger size="small" sx={{ width: 28, height: 28, padding: 0 }}>
            <MoreVertical size={16} />
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="end">
          {missing.map((connection) => (
            <DropdownMenuItem key={connection.key} onClick={() => onConnect(client, connection)}>
              <Box sx={{ display: "inline-flex", width: 18, justifyContent: "center", mr: 1 }}>
                {connection.icon}
              </Box>
              Conectar {connection.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  );
};

ConnectMenu.propTypes = {
  client: PropTypes.shape({ integrations: PropTypes.object }).isRequired,
  onConnect: PropTypes.func.isRequired,
};

const ClientCell = ({ client }) => {
  const { user } = useAuth();
  return (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <Avatar
      sx={{ width: "24px", height: "24px", flexShrink: 0 }}
      src={getAvatarImageUrl(client.logo) || undefined}
    >
      <AvatarFallback
        sx={{ backgroundColor: brand.avatar, color: "#fff", fontSize: "0.625rem", fontWeight: 600 }}
      >
        {(client.name || "")
          .split(" ")
          .map((part) => part[0])
          .join("")}
      </AvatarFallback>
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}
        >
          {client.name}
        </Typography>
        {isClientReadOnly(user, client) && <ReadOnlyBadge />}
      </Box>
      {(client.email || client.contact_name) && (
        <Typography
          sx={{ fontSize: "0.75rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}
        >
          {client.email || client.contact_name}
        </Typography>
      )}
    </Box>
  </Box>
  );
};

ClientCell.propTypes = {
  client: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string,
    email: PropTypes.string,
    contact_name: PropTypes.string,
  }).isRequired,
};

/**
 * Analíticas de los clientes: tabla de clientes y, al abrir uno, sus paneles de
 * visibilidad, tráfico y rendimiento web.
 */
export function Analytics() {
  const { user: currentUser, interceptorReady } = useAuth();
  const theme = useMuiTheme();
  const { setPageContext, selectedFilter, selectedFilterData } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  // { client, connection } cuyo modal de conexión está abierto
  const [connecting, setConnecting] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  // Un único rango para todas las pestañas y clientes.
  const [dates, setDates] = usePersistedDateSelection(DATE_SELECTION_STORAGE_KEY);

  const clientId = searchParams.get("clientId") ? parseInt(searchParams.get("clientId"), 10) : null;
  const tabParam = searchParams.get("tab");
  // Lanzar auditorías es cosa del equipo interno: el cliente no ve la pestaña.
  const tabs = React.useMemo(
    () => ANALYTICS_TABS.filter((tab) => tab.value !== "auditorias" || currentUser?.role !== "client"),
    [currentUser?.role]
  );
  const activeTab = tabs.some((tab) => tab.value === tabParam) ? tabParam : "visibilidad";
  const isDetailView = Boolean(clientId);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchTerm(trimmed.length >= 2 ? trimmed : "");
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    setPage(0);
  }, [selectedFilter]);

  const listEndpoint = React.useMemo(() => {
    if (isDetailView) return null;
    const params = new URLSearchParams({
      expand: "integrations",
      archived: "false",
      ordering: "name",
      page: String(page + 1),
      page_size: String(rowsPerPage),
    });
    if (searchTerm) params.set("search", searchTerm);
    if (selectedFilter) {
      // El filtro global es una agencia para superadmin y un cliente para el resto
      params.set(currentUser?.role === "superadmin" ? "agency" : "client_id", selectedFilter);
    }
    return `clients/?${params.toString()}`;
  }, [isDetailView, page, rowsPerPage, searchTerm, selectedFilter, currentUser?.role]);

  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
    isFetching: listFetching,
  } = getData(listEndpoint, interceptorReady && Boolean(listEndpoint), [listEndpoint, listRefreshKey]);

  const {
    data: clientData,
    error: clientError,
  } = getData(
    clientId ? `clients/${clientId}/` : null,
    interceptorReady && Boolean(clientId),
    [clientId]
  );

  const rows = Array.isArray(listData) ? listData : listData?.results || [];
  const totalCount = typeof listData?.count === "number" ? listData.count : rows.length;

  const openClient = React.useCallback(
    (client) => setSearchParams({ clientId: String(client.id), tab: "visibilidad" }),
    [setSearchParams]
  );

  const handleTabChange = React.useCallback(
    (tab) => setSearchParams({ clientId: String(clientId), tab }),
    [setSearchParams, clientId]
  );

  const handleConnect = React.useCallback(
    (client, connection) => setConnecting({ client, connection }),
    []
  );

  const ConnectModal = connecting?.connection.Modal;

  const handleBack = React.useCallback(() => setSearchParams({}), [setSearchParams]);

  React.useEffect(() => {
    if (!setPageContext) return;

    if (isDetailView) {
      setPageContext({
        title: (
          <>
            Analíticas:{"  "}
            <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>
              {clientData?.name || ""}
            </span>
          </>
        ),
        subtitle: TAB_SUBTITLES[activeTab],
        showTitle: true,
        leftContent: (
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              color: "primary.main",
              padding: "4px",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ChevronLeft size={20} />
          </IconButton>
        ),
        rightContent: null,
        titleRightContent: null,
        breadcrumbItems: [
          { label: "Analíticas", onClick: handleBack },
          { label: `Cliente: ${clientData?.name || ""}` },
        ],
      });
      return;
    }

    setPageContext({
      title: "Analíticas",
      subtitle: "Visibilidad, tráfico y rendimiento web de tus clientes",
      showTitle: true,
      leftContent: null,
      rightContent: null,
      titleRightContent: null,
      breadcrumbItems:
        currentUser?.role === "superadmin" && selectedFilter && selectedFilterData
          ? [{ label: "Vista Global" }, { label: `Agencia: ${selectedFilterData.name}` }]
          : [{ label: "Vista Global" }],
    });
  }, [
    setPageContext,
    isDetailView,
    clientData?.name,
    activeTab,
    handleBack,
    theme,
    currentUser?.role,
    selectedFilter,
    selectedFilterData,
  ]);

  const columns = React.useMemo(
    () => [
      { key: "client", header: "Cliente", minWidth: 280, render: (client) => <ClientCell client={client} /> },
      {
        key: "website",
        header: "Web",
        render: (client) =>
          client.website ? (
            <Typography
              sx={{ fontSize: "0.8125rem", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 260 }}
            >
              {client.website}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>Sin web</Typography>
          ),
      },
      {
        key: "agency",
        header: "Agencia",
        hidden: !(currentUser?.role === "superadmin" && !selectedFilter),
        render: (client) => client.agency_name || "Sin agencia",
      },
      {
        key: "connections",
        header: "Conexiones",
        tooltip: `Google Analytics, Search Console, ${getSeoProvider(currentUser).label} y PageSpeed`,
        render: (client) => <ConnectionsCell integrations={client.integrations} />,
      },
      {
        key: "actions",
        header: "",
        align: "right",
        width: 56,
        render: (client) => <ConnectMenu client={client} onConnect={handleConnect} />,
      },
    ],
    [currentUser?.role, selectedFilter, handleConnect]
  );

  if (isDetailView) {
    if (clientError) {
      return <Alert severity="error">Error al cargar el cliente. Inténtalo de nuevo.</Alert>;
    }

    return (
      <Stack spacing={2}>
        <UnderlineTabs tabs={tabs} value={activeTab} onChange={handleTabChange} />

        {activeTab === "visibilidad" && (
          <VisibilityPanel
            key={`visibility-${clientId}`}
            clientId={clientId}
            dates={dates}
            onDatesChange={setDates}
          />
        )}

        {activeTab === "trafico" && (
          <TrafficPanel
            key={`traffic-${clientId}`}
            clientId={clientId}
            dates={dates}
            onDatesChange={setDates}
          />
        )}

        {activeTab === "rendimiento" && (
          <WebPerformancePanel
            key={`performance-${clientId}`}
            clientId={clientId}
            website={clientData?.website || ""}
          />
        )}

        {activeTab === "auditorias" && (
          <SiteAuditPanel
            key={`audits-${clientId}`}
            clientId={clientId}
            website={clientData?.website || ""}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {listError && <Alert severity="error">Error al cargar los clientes. Inténtalo de nuevo.</Alert>}
      <DataTable
        columns={columns}
        rows={rows}
        loading={listLoading || listFetching}
        skeletonRows={8}
        emptyIcon={BarChart3}
        emptyMessage="No hay clientes"
        tableMinWidth={900}
        onRowClick={openClient}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar cliente..."
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(0);
        }}
      />

      {ConnectModal && (
        <ConnectModal
          open
          onOpenChange={(open) => !open && setConnecting(null)}
          clientId={connecting.client.id}
          client={connecting.client}
          currentLink={null}
          onConnected={() => {
            setNotification(`${connecting.connection.label} conectado a ${connecting.client.name}`);
            setListRefreshKey((key) => key + 1);
          }}
          onDisconnected={() => setListRefreshKey((key) => key + 1)}
        />
      )}

      <SuccessPopup
        message={notification || ""}
        isOpen={Boolean(notification)}
        onClose={() => setNotification(null)}
      />
    </Stack>
  );
}

export default Analytics;
